
import React, { useState, useRef, useEffect } from 'react';
import { RADII, TRANSITIONS, SHADOWS } from '../constants/theme';
import { ChevronDown, Check, ChevronRight, ArrowLeft } from './Icons';

interface CardProps {
  children?: React.ReactNode;
  className?: string;
  variant?: 'white' | 'dark' | 'glass';
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  overflowHidden?: boolean;
}

export const Card: React.FC<CardProps> = ({ 
  children, 
  className = '', 
  variant = 'white', 
  onClick,
  overflowHidden = false
}) => {
  const variants = {
    // Updated dark mode to #111111 for surface with subtle #262626 border
    white: `bg-white border border-gray-200 dark:bg-[#111111] dark:border-[#262626] ${SHADOWS.md}`,
    dark: `bg-[#111111] text-white border border-[#262626] ${SHADOWS.md}`,
    glass: 'bg-white/90 backdrop-blur-md border border-gray-200',
  };

  return (
    <div 
      onClick={onClick}
      className={`${RADII.standard} ${variants[variant]} ${className} ${overflowHidden ? 'overflow-hidden' : ''} ${onClick ? 'cursor-pointer hover:border-brand-dark/30 dark:hover:border-brand-medium hover:-translate-y-0.5 transition-transform duration-300' : ''}`}
    >
      {children}
    </div>
  );
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  className = '',
  ...props 
}) => {
  const base = `font-semibold tracking-wide ${TRANSITIONS} flex items-center justify-center whitespace-nowrap active:scale-[0.98] ${RADII.element}`;
  
  const variants = {
    primary: 'bg-[#474D22] text-white hover:bg-[#3A3F1C] border border-transparent',
    secondary: 'bg-[#393939] text-white hover:bg-[#4c4c4c]',
    outline: 'bg-transparent border border-[#474D22] text-[#474D22] hover:bg-[#474D22]/5 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-[#262626] dark:hover:border-gray-400',
    ghost: 'bg-transparent text-[#474D22] hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#262626] dark:hover:text-white',
  };

  const sizes = {
    sm: 'px-4 h-8 text-xs',
    md: 'px-5 h-10 text-sm', 
    lg: 'px-8 h-12 text-base', 
  };

  return (
    <button className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...props}>
      {children}
    </button>
  );
};

export const Toggle: React.FC<{ 
  label: string; 
  enabled: boolean; 
  onChange: (v: boolean) => void;
  className?: string;
}> = ({ label, enabled, onChange, className = '' }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <button 
      onClick={() => onChange(!enabled)}
      className={`relative w-11 h-6 transition-colors duration-300 focus:outline-none rounded-full ${enabled ? 'bg-[#474D22]' : 'bg-gray-200 dark:bg-[#333333]'}`}
    >
      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
    {label && <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">{label}</span>}
  </div>
);

export const Radio: React.FC<{ 
  label: string; 
  checked: boolean; 
  onClick: () => void; 
  icon?: React.ReactNode;
}> = ({ label, checked, onClick, icon }) => (
  <button 
    onClick={onClick}
    className="flex items-center gap-2 group focus:outline-none"
  >
    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${checked ? 'border-[#474D22]' : 'border-gray-300 dark:border-[#444] group-hover:border-gray-400'}`}>
      {checked && <div className="w-2.5 h-2.5 rounded-full bg-[#474D22]" />}
    </div>
    <span className={`text-sm font-medium flex items-center gap-2 ${checked ? 'text-[#474D22] dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
      {label}
      {icon}
    </span>
  </button>
);

export const SelectionCard: React.FC<{
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}> = ({ selected, onClick, children, className = '' }) => (
  <div
    onClick={onClick}
    className={`cursor-pointer border-2 ${RADII.standard} px-6 py-4 flex items-center gap-4 transition-all duration-200 min-w-[200px] justify-center ${
      selected
        ? 'border-[#474D22] bg-white dark:bg-[#1A1A1A] text-[#474D22] dark:text-white shadow-none'
        : 'border-gray-200 dark:border-[#262626] bg-white dark:bg-[#111111] text-gray-500 hover:border-gray-300 dark:hover:border-[#333]'
    } ${className}`}
  >
    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
      selected ? 'border-[#474D22]' : 'border-gray-300 dark:border-[#444]'
    }`}>
      {selected && <div className="w-2.5 h-2.5 rounded-full bg-[#474D22]" />}
    </div>
    <span className="text-xs font-bold uppercase tracking-widest">{children}</span>
  </div>
);

interface CustomSelectProps {
  label: string;
  value: string;
  options: string[];
  onSelect: (val: string) => void;
  placeholder?: string;
  className?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({ label, value, options, onSelect, placeholder, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative flex flex-col gap-1.5 ${className}`} ref={containerRef}>
      <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest ml-1">{label}</label>
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between bg-white dark:bg-[#161616] border px-4 h-11 text-sm cursor-pointer transition-all ${RADII.element} ${
          isOpen ? 'border-[#474D22] ring-2 ring-[#474D22]/10' : 'border-gray-200 hover:border-gray-300 dark:border-[#333] dark:hover:border-[#444]'
        }`}
      >
        <span className="truncate text-[#161616] dark:text-gray-200 font-medium">{value || placeholder}</span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className={`absolute top-full left-0 right-0 mt-2 bg-white dark:bg-[#161616] z-[100] border border-gray-100 dark:border-[#333] ${RADII.element} overflow-hidden p-1 shadow-lg`}>
          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {options.map((option) => (
              <div
                key={option}
                onClick={() => {
                  onSelect(option);
                  setIsOpen(false);
                }}
                className={`px-3 py-2.5 flex items-center text-sm font-medium cursor-pointer rounded-md transition-colors ${value === option ? 'bg-brand-light/50 dark:bg-[#262626] text-[#474D22] dark:text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#262626]'}`}
              >
                {option}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { label?: string }> = ({ label, ...props }) => (
  <div className="space-y-1.5 flex-1">
    {label && <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest ml-1">{label}</label>}
    <div className="relative">
      <select 
        {...props}
        className={`w-full bg-white dark:bg-[#161616] border border-gray-200 dark:border-[#333] ${RADII.element} px-4 h-11 text-sm font-medium text-[#161616] dark:text-gray-200 focus:outline-none focus:border-[#474D22] focus:ring-2 focus:ring-[#474D22]/10 transition-all appearance-none cursor-pointer ${props.className || ''}`}
      >
        {props.children}
      </select>
      <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
  </div>
);

interface BreadcrumbItem {
  label: string;
  active?: boolean;
}

interface PageHeaderProps {
  title: string;
  breadcrumbs: BreadcrumbItem[];
  actions?: React.ReactNode;
  onBack?: () => void;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, breadcrumbs, actions, onBack }) => (
  <div className="flex flex-col gap-2 mb-8 pb-5">
    <div className="flex items-center gap-2 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
      {breadcrumbs.map((crumb, idx) => (
        <React.Fragment key={crumb.label}>
          <span className={idx === breadcrumbs.length - 1 ? 'text-[#474D22] dark:text-white' : 'hover:text-[#161616] dark:hover:text-gray-300 cursor-pointer transition-colors'}>
            {crumb.label}
          </span>
          {idx < breadcrumbs.length - 1 && (
            <ChevronRight size={10} className="text-gray-300 dark:text-gray-600" />
          )}
        </React.Fragment>
      ))}
    </div>
    
    <div className="flex items-center justify-between mt-2">
      <div className="flex items-center gap-4">
        {onBack && (
          <button 
            onClick={onBack}
            className="w-10 h-10 flex items-center justify-center text-[#474D22] dark:text-white bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#262626] hover:border-gray-300 dark:hover:border-[#444] rounded-full transition-all"
            title="Go Back"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
        )}
        <h1 className="text-3xl font-light text-[#161616] dark:text-white tracking-tight">
          {title}
        </h1>
      </div>
      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
  </div>
);

export const Table: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`overflow-x-auto w-full`}>
    <table className={`w-full text-left border-collapse ${className}`}>
      {children}
    </table>
  </div>
);

export const Thead: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <thead className={`bg-[#FAFBFC] dark:bg-[#121212] border-b border-gray-200 dark:border-[#262626] ${className}`}>
    {children}
  </thead>
);

export const Tbody: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <tbody className={`divide-y divide-gray-100 dark:divide-[#1A1A1A] ${className}`}>
    {children}
  </tbody>
);

export const Tr: React.FC<{ children: React.ReactNode; className?: string; isHeader?: boolean; onClick?: React.MouseEventHandler<HTMLTableRowElement> }> = ({ children, className = '', isHeader = false, onClick }) => (
  <tr 
    onClick={onClick}
    className={`${!isHeader ? 'hover:bg-gray-50 dark:hover:bg-[#1A1A1A] transition-colors duration-150' : ''} ${onClick ? 'cursor-pointer' : ''} ${className}`}
  >
    {children}
  </tr>
);

export const Th: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th className={`px-6 !py-1 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest whitespace-nowrap !align-middle ${className}`}>
    {children}
  </th>
);

export const Td: React.FC<React.TdHTMLAttributes<HTMLTableDataCellElement>> = ({ children, className = '', ...props }) => (
  <td className={`px-6 h-14 text-sm font-medium text-[#161616] dark:text-gray-300 ${className}`} {...props}>
    {children}
  </td>
);

export const Badge: React.FC<{ children: React.ReactNode; className?: string; color?: string }> = ({ 
  children, 
  className = '',
  color
}) => (
  <div 
    className={`px-2.5 py-1 flex items-center justify-center shrink-0 text-[10px] font-bold uppercase tracking-wider ${RADII.tiny} ${className}`}
    style={color ? { backgroundColor: color, color: '#fff' } : {}}
  >
    {children}
  </div>
);

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input 
    {...props}
    className={`w-full bg-white dark:bg-[#161616] border border-gray-200 dark:border-[#333] ${RADII.element} px-4 h-11 text-sm font-medium text-[#161616] dark:text-gray-200 focus:outline-none focus:border-[#474D22] focus:ring-2 focus:ring-[#474D22]/10 placeholder:text-gray-400 dark:placeholder:text-gray-600 transition-all ${props.className || ''}`}
  />
);
